import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const PageContainer = styled.div`
  padding: 0 0 3rem;
`;

const ContentSection = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  color: var(--dark);
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const SubsectionTitle = styled.h3`
  color: var(--dark);
  margin: 1.5rem 0 1rem;
  font-size: 1.3rem;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: var(--gray);
  font-style: italic;
  margin-bottom: 2rem;
`;

const PrivacyPolicyPage = () => {
  return (
    <>
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Privacy Policy</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
          </p>
        </div>
      </section>
      
      <div className="container">
        <PageContainer>
          <LastUpdated>Last Updated: March 31, 2025</LastUpdated>
          
          <ContentSection>
            <SectionTitle>1. Introduction</SectionTitle>
            <p>At Dozzy.au ("we," "our," or "us"), we respect your privacy and are committed to protecting it through our compliance with this policy.</p>
            <p>This Privacy Policy describes the types of information we may collect from you or that you may provide when you visit or use the Dozzy.au website and application (collectively, the "Service") and our practices for collecting, using, maintaining, protecting, and disclosing that information.</p>
            <p>Please read this policy carefully to understand our policies and practices regarding your information. If you do not agree with our policies and practices, your choice is not to use our Service. By accessing or using the Service, you agree to this Privacy Policy.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>2. Information We Collect</SectionTitle>
            
            <SubsectionTitle>2.1 Personal Information</SubsectionTitle>
            <p>We may collect several types of information from and about users of our Service, including:</p>
            <ul>
              <li><strong>Account Information:</strong> When you register for an account, we collect your name, email address, password, and location information (city, state, postcode).</li>
              <li><strong>Child Information:</strong> To provide personalized sleep plans, we collect information about your child, including name, age, sleep patterns, health information, feeding habits, and sleep environment details.</li>
              <li><strong>Payment Information:</strong> When you make a purchase, our payment processor (Stripe) collects payment information such as credit card details. We do not store complete payment information on our servers.</li>
              <li><strong>Communications:</strong> If you contact us directly, we may receive additional information about you, such as your name, email address, phone number, the contents of the message and/or attachments you may send us, and any other information you may choose to provide.</li>
            </ul>
            
            <SubsectionTitle>2.2 Usage Information</SubsectionTitle>
            <p>We automatically collect certain information about your device and how you interact with our Service, including:</p>
            <ul>
              <li>IP address and device identifiers</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages you visit on our Service and how you interact with them</li>
              <li>Time and date of your visits</li>
              <li>Referral source</li>
            </ul>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>3. How We Use Your Information</SectionTitle>
            <p>We use the information we collect about you or that you provide to us for the following purposes:</p>
            <ul>
              <li>To provide, maintain, and improve our Service</li>
              <li>To create and manage your account</li>
              <li>To generate personalized sleep plans for your child</li>
              <li>To process your payments and provide you with receipts</li>
              <li>To communicate with you about your account, our Service, and updates</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To send you technical notices, updates, security alerts, and administrative messages</li>
              <li>To monitor and analyze usage patterns and trends to improve our Service</li>
              <li>To protect our Service, users, and the public</li>
              <li>To comply with applicable laws, regulations, and legal processes</li>
            </ul>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>4. Data Security</SectionTitle>
            
            <SubsectionTitle>4.1 Security Measures</SubsectionTitle>
            <p>We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. These measures include:</p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted both in transit (using TLS/SSL) and at rest using industry-standard encryption methods.</li>
              <li><strong>Access Controls:</strong> We restrict access to personal information to authorized personnel only, based on job function and role.</li>
              <li><strong>AWS Infrastructure:</strong> Our Service is hosted on Amazon Web Services (AWS), which maintains SOC 1, SOC 2, and SOC 3 certifications, as well as ISO 27001, 27017, and 27018 certifications.</li>
              <li><strong>Regular Security Assessments:</strong> We conduct regular security assessments and penetration testing to identify and address potential vulnerabilities.</li>
            </ul>
            
            <SubsectionTitle>4.2 Data Breach Procedures</SubsectionTitle>
            <p>In the event of a data breach that affects your personal information, we will notify you and the relevant authorities as required by applicable law. We have procedures in place to respond to suspected data breaches promptly.</p>
            
            <SubsectionTitle>4.3 Limitations</SubsectionTitle>
            <p>While we implement safeguards designed to protect your information, no security system is impenetrable. Due to the inherent nature of the Internet, we cannot guarantee that information, during transmission through the Internet or while stored on our systems or otherwise in our care, is absolutely safe from intrusion by others.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>5. Data Retention</SectionTitle>
            <p>We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.</p>
            <p>If you request deletion of your account, we will delete your personal information from our active databases within 30 days. However, some information may be retained in our files to prevent fraud, troubleshoot problems, assist with investigations, enforce our Terms of Use, or comply with legal requirements.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>6. Disclosure of Your Information</SectionTitle>
            
            <SubsectionTitle>6.1 Service Providers</SubsectionTitle>
            <p>We may share your information with third-party service providers who perform services on our behalf, such as:</p>
            <ul>
              <li>Payment processing (Stripe)</li>
              <li>Cloud hosting and storage (AWS)</li>
              <li>Analytics services</li>
              <li>Customer support services</li>
              <li>Email delivery services</li>
            </ul>
            <p>These service providers are contractually obligated to use your information only as directed by us and for the purpose of providing services to us.</p>
            
            <SubsectionTitle>6.2 Legal Requirements</SubsectionTitle>
            <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</p>
            
            <SubsectionTitle>6.3 Business Transfers</SubsectionTitle>
            <p>If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on our Service of any change in ownership or uses of your personal information.</p>
            
            <SubsectionTitle>6.4 With Your Consent</SubsectionTitle>
            <p>We may disclose your personal information for any other purpose with your consent.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>7. Your Rights and Choices</SectionTitle>
            
            <SubsectionTitle>7.1 Account Information</SubsectionTitle>
            <p>You can review and update your account information by logging into your account. If you would like to delete your account, please email us at support@dozzy.au.</p>
            
            <SubsectionTitle>7.2 Communication Preferences</SubsectionTitle>
            <p>You can opt out of receiving promotional emails from us by following the instructions in those emails. If you opt out, we may still send you non-promotional emails, such as those about your account or our ongoing business relations.</p>
            
            <SubsectionTitle>7.3 Cookies</SubsectionTitle>
            <p>Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove or reject cookies. Please note that if you choose to remove or reject cookies, this could affect the availability and functionality of our Service.</p>
            
            <SubsectionTitle>7.4 Your Privacy Rights</SubsectionTitle>
            <p>Depending on your location, you may have certain rights regarding your personal information, such as:</p>
            <ul>
              <li>The right to access the personal information we have about you</li>
              <li>The right to request correction of inaccurate personal information</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to object to processing of your personal information</li>
              <li>The right to data portability</li>
              <li>The right to withdraw consent</li>
            </ul>
            <p>To exercise these rights, please contact us at support@dozzy.au.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>8. Children's Privacy</SectionTitle>
            <p>Our Service is intended for use by adults (persons 18 years of age or older). We do not knowingly collect personal information from children under 18. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us at support@dozzy.au, and we will take steps to delete such information.</p>
            <p>The information we collect about children is provided by their parents or legal guardians for the purpose of generating personalized sleep plans. We treat this information with the utmost care and in accordance with this Privacy Policy.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>9. International Data Transfers</SectionTitle>
            <p>Your information may be transferred to, and maintained on, computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction.</p>
            <p>If you are located outside Australia and choose to provide information to us, please note that we transfer the data, including personal information, to Australia and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>10. Changes to Our Privacy Policy</SectionTitle>
            <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page.</p>
            <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>11. Contact Us</SectionTitle>
            <p>If you have any questions about this Privacy Policy, please contact us at:</p>
            <p>Email: support@dozzy.au</p>
            
          </ContentSection>
        </PageContainer>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
